import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CheckCircle, XCircle, Loader2, Sheet, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { schedulesApi, queryKeys } from '@/lib/api';
import { fmtInTz } from '@/lib/utils';

interface CsvImportModalProps {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  facilityTimezone?: string;
  /** When set, the modal pulls directly from Google Sheets instead of showing a textarea */
  googleSheetsUrl?: string | null;
  ccmApiUrl?: string | null;
  leagueAppsConfigured?: boolean;
  curlOpsConfigured?: boolean;
  scheduleSource?: 'manual' | 'google_sheets' | 'ccm_api' | 'leagueapps_api' | 'generic_recurring' | 'curlops_api';
}

interface PreviewRow {
  rowNumber: number;
  eventName?: string;
  sheetId?: string;
  team1Name?: string;
  team2Name?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  notes?: string;
  valid: boolean;
  errors?: string[];
  conflictNote?: string;
  [key: string]: unknown;
}

type ImportResult = { preview?: PreviewRow[]; imported?: number; deleted?: number; schedules?: unknown[]; timezone?: string; skippedPast?: number; invalidRows?: number; gamesFetched?: number; note?: string; windowDays?: number; skippedOutOfWindow?: number; suppressedByTombstone?: number; clampedRows?: number; diff?: { create: number; update: number; remove: number; unchanged: number } };

export default function CsvImportModal({ open, onClose, facilityId, facilityTimezone, googleSheetsUrl, ccmApiUrl, leagueAppsConfigured, curlOpsConfigured, scheduleSource }: CsvImportModalProps) {
  const queryClient = useQueryClient();
  const isSheetMode   = scheduleSource === 'google_sheets' && !!googleSheetsUrl;
  const isCcmMode     = scheduleSource === 'ccm_api' && !!ccmApiUrl;
  const isLaMode      = scheduleSource === 'leagueapps_api' && !!leagueAppsConfigured;
  const isCurlOpsMode = scheduleSource === 'curlops_api' && !!curlOpsConfigured;

  const [csvText, setCsvText] = useState('');
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [windowInfo, setWindowInfo] = useState<{ windowDays?: number | undefined; skippedOutOfWindow?: number | undefined; suppressedByTombstone?: number | undefined; clampedRows?: number | undefined; diff?: { create: number; update: number; remove: number; unchanged: number } | undefined }>({});
  const [detectedTimezone, setDetectedTimezone] = useState<string | undefined>(facilityTimezone);
  const [step, setStep] = useState<'input' | 'preview' | 'done'>('input');
  const [importedCount, setImportedCount] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [skippedBye, setSkippedBye] = useState(0);
  const [diagnosticNote, setDiagnosticNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // In Sheets/CCM mode, auto-fetch preview when modal opens
  useEffect(() => {
    if (open && step === 'input') {
      if (isSheetMode) sheetPreviewMutation.mutate();
      else if (isCcmMode) ccmPreviewMutation.mutate();
      else if (isLaMode) laPreviewMutation.mutate();
      else if (isCurlOpsMode) curlOpsPreviewMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePreviewSuccess = (data: ImportResult) => {
    const rows = (data['preview'] || []) as PreviewRow[];
    setPreviewRows(rows);
    if (data.timezone) setDetectedTimezone(data.timezone);
    setDiagnosticNote(data.note ?? null);
    setWindowInfo({
      windowDays: data.windowDays,
      skippedOutOfWindow: data.skippedOutOfWindow,
      suppressedByTombstone: data.suppressedByTombstone,
      clampedRows: data.clampedRows,
      diff: data.diff,
    });
    setStep('preview');
    setError(null);
  };

  const handleImportSuccess = (data: ImportResult) => {
    const count = (data['imported'] as number | undefined) ?? ((data['schedules'] as unknown[])?.length || 0);
    setImportedCount(count);
    setDeletedCount(data['deleted'] ?? 0);
    setSkippedBye((data as any)['skippedBye'] ?? 0);
    setDiagnosticNote(data.note ?? null);
    setStep('done');
    queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all });
  };

  // CSV paste mode
  const previewMutation = useMutation({
    mutationFn: () => schedulesApi.importCsv(facilityId, csvText, false) as Promise<ImportResult>,
    onSuccess: handlePreviewSuccess,
    onError: (err: any) => setError(err?.message || 'Failed to parse CSV'),
  });

  const importMutation = useMutation({
    mutationFn: () => schedulesApi.importCsv(facilityId, csvText, true) as Promise<ImportResult>,
    onSuccess: handleImportSuccess,
    onError: (err: any) => setError(err?.message || 'Import failed'),
  });

  // Google Sheets mode
  const sheetPreviewMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromSheets(facilityId, false) as Promise<ImportResult>,
    onSuccess: handlePreviewSuccess,
    onError: (err: any) => setError(err?.message || 'Failed to fetch Google Sheet'),
  });

  const sheetImportMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromSheets(facilityId, true) as Promise<ImportResult>,
    onSuccess: handleImportSuccess,
    onError: (err: any) => setError(err?.message || 'Import failed'),
  });

  // CCM mode
  const ccmPreviewMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromCcm(facilityId, false) as Promise<ImportResult>,
    onSuccess: handlePreviewSuccess,
    onError: (err: any) => setError(err?.message || 'Failed to fetch CCM schedule'),
  });

  const ccmImportMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromCcm(facilityId, true) as Promise<ImportResult>,
    onSuccess: handleImportSuccess,
    onError: (err: any) => setError(err?.message || 'Import failed'),
  });

  // LeagueApps mode
  const laPreviewMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromLeagueApps(facilityId, false) as Promise<ImportResult>,
    onSuccess: handlePreviewSuccess,
    onError: (err: any) => setError(err?.message || 'Failed to fetch LeagueApps schedule'),
  });

  const laImportMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromLeagueApps(facilityId, true) as Promise<ImportResult>,
    onSuccess: handleImportSuccess,
    onError: (err: any) => setError(err?.message || 'Import failed'),
  });

  // CurlOps mode
  const curlOpsPreviewMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromCurlOps(facilityId, false) as Promise<ImportResult>,
    onSuccess: handlePreviewSuccess,
    onError: (err: any) => setError(err?.message || 'Failed to fetch CurlOps schedule'),
  });

  const curlOpsImportMutation = useMutation({
    mutationFn: () => schedulesApi.syncFromCurlOps(facilityId, true) as Promise<ImportResult>,
    onSuccess: handleImportSuccess,
    onError: (err: any) => setError(err?.message || 'Import failed'),
  });

  const handleClose = () => {
    setCsvText('');
    setPreviewRows([]);
    setStep('input');
    setError(null);
    setImportedCount(0);
    setDeletedCount(0);
    setSkippedBye(0);
    setDiagnosticNote(null);
    onClose();
  };

  const handlePreview = () => {
    setError(null);
    if (isSheetMode) sheetPreviewMutation.mutate();
    else if (isCcmMode) ccmPreviewMutation.mutate();
    else if (isLaMode) laPreviewMutation.mutate();
    else if (isCurlOpsMode) curlOpsPreviewMutation.mutate();
    else previewMutation.mutate();
  };

  const handleImport = () => {
    if (isSheetMode) sheetImportMutation.mutate();
    else if (isCcmMode) ccmImportMutation.mutate();
    else if (isLaMode) laImportMutation.mutate();
    else if (isCurlOpsMode) curlOpsImportMutation.mutate();
    else importMutation.mutate();
  };

  const isPreviewing = previewMutation.isPending || sheetPreviewMutation.isPending || ccmPreviewMutation.isPending || laPreviewMutation.isPending || curlOpsPreviewMutation.isPending;
  const isImporting = importMutation.isPending || sheetImportMutation.isPending || ccmImportMutation.isPending || laImportMutation.isPending || curlOpsImportMutation.isPending;

  const validRows = previewRows.filter(r => r.valid);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex w-full max-w-3xl flex-col rounded-lg bg-white text-gray-900 shadow-xl dark:bg-gray-900 dark:text-gray-100" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold">
            {isSheetMode ? 'Import Schedules from Google Sheets' : isCcmMode ? 'Import Schedules from CurlingClubManager' : isLaMode ? 'Import Schedules from LeagueApps' : isCurlOpsMode ? 'Import Schedules from CurlOps' : 'Import Schedules from CSV'}
          </h2>
          <button
            onClick={handleClose}
            className="rounded-md p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}

          {step === 'input' && (
            <div className="space-y-4">
              {isSheetMode || isCcmMode || isLaMode || isCurlOpsMode ? (
                /* Auto-fetch mode — Google Sheets, CCM, or LeagueApps */
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isCcmMode ? 'Fetching schedule from CurlingClubManager…'
                      : isLaMode ? 'Fetching schedule from LeagueApps…'
                      : isCurlOpsMode ? 'Fetching schedule from CurlOps…'
                      : 'Fetching schedule from Google Sheets…'}
                  </p>
                  <p className="max-w-sm truncate text-xs text-gray-400 dark:text-gray-500">
                    {isCcmMode ? ccmApiUrl : isLaMode ? 'public.leagueapps.io' : isCurlOpsMode ? 'api.curlops.com' : googleSheetsUrl}
                  </p>
                </div>
              ) : (
                /* Manual CSV paste mode */
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Paste CSV data below. Required columns:
                  </p>
                  <code className="block rounded bg-gray-100 px-3 py-2 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    eventName, sheetId, scheduledStart, scheduledEnd
                  </code>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Optional columns: <code className="text-xs">team1Name, team2Name, notes</code>
                  </p>
                  <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800/50 dark:bg-blue-950/30 dark:text-blue-300">
                    <strong>Times are interpreted as facility-local time</strong>
                    {detectedTimezone && <span className="ml-1">(<span className="font-mono">{detectedTimezone}</span>).</span>}
                    {' '}Enter times in 24-hour format — no timezone suffix required.
                    <br />
                    Format: <code>YYYY-MM-DD HH:MM</code> — e.g. <code>2026-04-15 09:00</code>
                  </div>
                  <textarea
                    value={csvText}
                    onChange={e => setCsvText(e.target.value)}
                    rows={10}
                    placeholder={`eventName,sheetId,team1Name,team2Name,scheduledStart,scheduledEnd,notes\nMorning Draw,A,Smith,Jones,2026-04-15 09:00,2026-04-15 11:00,Round 1\nAfternoon Draw,A,Williams,Davis,2026-04-15 13:00,2026-04-15 15:00,Round 2`}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-3">
              {diagnosticNote && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300">
                  {diagnosticNote}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                {isSheetMode && (
                  <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Sheet className="h-3.5 w-3.5" />
                    Google Sheets
                  </span>
                )}
                {isCcmMode && (
                  <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Link className="h-3.5 w-3.5" />
                    CurlingClubManager
                  </span>
                )}
                {isLaMode && (
                  <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Link className="h-3.5 w-3.5" />
                    LeagueApps
                  </span>
                )}
                {isCurlOpsMode && (
                  <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Link className="h-3.5 w-3.5" />
                    CurlOps
                  </span>
                )}
                <span className="font-medium">{previewRows.length} rows found</span>
                <span className="text-green-600 dark:text-green-400">{validRows.length} valid</span>
                {previewRows.length - validRows.length > 0 && (
                  <span className="text-red-600 dark:text-red-400">{previewRows.length - validRows.length} invalid</span>
                )}
                {previewRows.some(r => r.conflictNote) && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {previewRows.filter(r => r.conflictNote).length} start time(s) auto-adjusted
                  </span>
                )}
                {isSheetMode && windowInfo.windowDays != null && (
                  <span className="text-blue-600 dark:text-blue-400">
                    next {windowInfo.windowDays} days
                    {windowInfo.diff && ` · ${windowInfo.diff.create} new, ${windowInfo.diff.update} updated, ${windowInfo.diff.remove} removed, ${windowInfo.diff.unchanged} unchanged`}
                    {windowInfo.skippedOutOfWindow ? ` · ${windowInfo.skippedOutOfWindow} beyond window (synced later)` : ''}
                    {windowInfo.suppressedByTombstone ? ` · ${windowInfo.suppressedByTombstone} kept deleted` : ''}
                    {windowInfo.clampedRows ? ` · ${windowInfo.clampedRows} flagged: tight against the next game on that sheet` : ''}
                  </span>
                )}
                {detectedTimezone && (
                  <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                    Times as <span className="font-mono">{detectedTimezone}</span>
                  </span>
                )}
              </div>
              <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">#</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Event</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Sheet</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Teams</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Start</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">End</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {previewRows.map(row => (
                      <tr
                        key={row.rowNumber}
                        className={!row.valid ? 'bg-red-50 dark:bg-red-900/20' : row.conflictNote ? 'bg-amber-50 dark:bg-amber-900/10' : ''}
                      >
                        <td className="px-2 py-1.5 text-gray-500">{row.rowNumber}</td>
                        <td className="px-2 py-1.5">
                          {row.valid
                            ? row.conflictNote
                              ? <span title={row.conflictNote}><CheckCircle className="h-4 w-4 text-amber-500" /></span>
                              : <CheckCircle className="h-4 w-4 text-green-500" />
                            : <XCircle className="h-4 w-4 text-red-500" />}
                        </td>
                        <td className="px-2 py-1.5 font-medium">{row.eventName || '—'}</td>
                        <td className="px-2 py-1.5">{row.sheetId || '—'}</td>
                        <td className="px-2 py-1.5">
                          {row.team1Name && row.team2Name
                            ? `${row.team1Name} vs ${row.team2Name}`
                            : row.team1Name || row.team2Name || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">
                          {row.scheduledStart ? fmtInTz(row.scheduledStart, detectedTimezone) : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">
                          {row.scheduledEnd ? fmtInTz(row.scheduledEnd, detectedTimezone) : '—'}
                        </td>
                        <td className="px-2 py-1.5">
                          {row.errors && row.errors.length > 0 && (
                            <span className="text-red-600 dark:text-red-400">{row.errors.join(', ')}</span>
                          )}
                          {row.conflictNote && (
                            <span className="text-amber-700 dark:text-amber-400">{row.conflictNote}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="text-lg font-semibold">Import Complete</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Successfully imported <strong>{importedCount}</strong> schedule{importedCount !== 1 ? 's' : ''}.
              </p>
              {(isSheetMode || isCcmMode || isLaMode || isCurlOpsMode) && deletedCount > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {deletedCount} previous future schedule{deletedCount !== 1 ? 's' : ''} replaced.
                </p>
              )}
              {(isCcmMode || isCurlOpsMode) && skippedBye > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {skippedBye} BYE game{skippedBye !== 1 ? 's' : ''} skipped.
                </p>
              )}
              {diagnosticNote && (
                <div className="mt-2 max-w-md rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300">
                  {diagnosticNote}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            {step === 'preview' && (
              <Button variant="outline" onClick={() => { setStep('input'); setError(null); if (isSheetMode || isCcmMode || isLaMode || isCurlOpsMode) { setPreviewRows([]); } }} disabled={isImporting}>
                {isSheetMode || isCcmMode || isLaMode || isCurlOpsMode ? 'Refetch' : 'Back'}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleClose}>
              {step === 'done' ? 'Close' : 'Cancel'}
            </Button>

            {step === 'input' && !isSheetMode && !isCcmMode && !isLaMode && !isCurlOpsMode && (
              <Button
                onClick={handlePreview}
                disabled={!csvText.trim() || isPreviewing}
              >
                {isPreviewing
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Parsing...</>
                  : 'Preview'}
              </Button>
            )}

            {step === 'preview' && (
              <Button
                onClick={handleImport}
                disabled={validRows.length === 0 || isImporting}
              >
                {isImporting
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Syncing...</>
                  : isSheetMode
                    ? `Sync ${validRows.length} Game${validRows.length !== 1 ? 's' : ''}`
                    : (isCcmMode || isLaMode || isCurlOpsMode)
                      ? `Wipe & Import ${validRows.length} Row${validRows.length !== 1 ? 's' : ''}`
                      : `Import ${validRows.length} Row${validRows.length !== 1 ? 's' : ''}`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
